import React from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FiClock, FiCheck } from 'react-icons/fi';
import SEOPageLayout from '../components/SEOPageLayout';
import { tours, SITE_URL } from '../config/seo-data';

const TourPage = () => {
  const { slug } = useParams();
  const tour = tours.find((t) => t.slug === slug);

  if (!tour) {
    return <Navigate to="/private-tours" replace />;
  }

  const otherTours = tours.filter((t) => t.slug !== slug);

  return (
    <SEOPageLayout
      title={`${tour.name} | Private Corfu Tour with Driver - Lasmari Chauffeurs`}
      description={tour.description}
      keywords={`corfu ${tour.name.toLowerCase()}, corfu private tour, corfu sightseeing, corfu day tour`}
      canonical={`${SITE_URL}/tours/${tour.slug}`}
    >
      <section className="relative py-24 lg:py-36 bg-[#faf7f6]">
        <div className="relative z-10 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="flex items-center justify-center gap-4 mb-4">
            <span className="bg-[#fbeceb] text-[#ba1609] text-xs px-3 py-1 rounded-full uppercase tracking-wider">{tour.type}</span>
            <span className="text-[#5b4d4f] text-sm flex items-center gap-1"><FiClock className="w-4 h-4" /> {tour.duration}</span>
          </motion.div>
          <motion.h1 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="text-4xl lg:text-6xl font-light text-[#1a1012] mb-6">
            {tour.name}
          </motion.h1>
          <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="text-[#5b4d4f] text-lg max-w-3xl mx-auto mb-4 font-light">
            {tour.description}
          </motion.p>
          <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2 }} className="text-[#ba1609] text-lg font-light mb-8">
            Contact us for a personalised quote
          </motion.p>
          <a href="https://wa.me/447404560313" target="_blank" rel="noopener noreferrer" className="inline-block bg-[#ba1609] text-white px-8 py-3 rounded-full font-medium hover:bg-[#8e1107] transition-colors text-lg">
            Request a Quote
          </a>
        </div>
      </section>

      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-5 gap-12">
            <div className="lg:col-span-3">
              <h2 className="text-2xl font-light text-[#1a1012] mb-6">About This Tour</h2>
              <p className="text-[#5b4d4f] leading-relaxed mb-8">{tour.longDescription}</p>

              <h3 className="text-[#1a1012] font-medium mb-4">Tour Itinerary</h3>
              <div className="space-y-3">
                {tour.stops.map((stop, i) => (
                  <div key={stop} className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-[#fbeceb] border border-[#ba1609]/30 flex items-center justify-center shrink-0">
                      <span className="text-[#ba1609] text-xs font-medium">{i + 1}</span>
                    </div>
                    <span className="text-[#1a1012]">{stop}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="lg:col-span-2">
              <div className="bg-[#faf7f6] border border-black/10 rounded-xl p-6 sticky top-24 shadow-sm">
                <h3 className="text-[#1a1012] font-medium mb-4">What's Included</h3>
                <ul className="space-y-3">
                  {['Mercedes-Benz with A/C', 'English-speaking driver', 'Hotel or port pickup', 'Flexible itinerary', 'Water & refreshments', 'Photo stop opportunities'].map((item) => (
                    <li key={item} className="flex items-center gap-2 text-[#5b4d4f] text-sm">
                      <FiCheck className="w-4 h-4 text-[#ba1609] shrink-0" /> {item}
                    </li>
                  ))}
                </ul>
                <div className="mt-6 pt-4 border-t border-black/10">
                  <div className="flex justify-between text-sm mb-1">
                    <span className="text-[#5b4d4f]">Duration</span>
                    <span className="text-[#1a1012]">{tour.duration}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-[#5b4d4f]">Price</span>
                    <span className="text-[#ba1609] font-medium">Contact for quote</span>
                  </div>
                </div>
                <a href="https://wa.me/447404560313" target="_blank" rel="noopener noreferrer" className="block w-full mt-6 bg-[#ba1609] text-white text-center py-3 rounded-full font-medium hover:bg-[#8e1107] transition-colors">
                  Request a Quote
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {otherTours.length > 0 && (
        <section className="py-16 bg-[#faf7f6]">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-2xl font-light text-[#1a1012] mb-8">Other Tours</h2>
            <div className="grid sm:grid-cols-3 gap-4">
              {otherTours.map((t) => (
                <Link key={t.slug} to={`/tours/${t.slug}`} className="bg-white border border-black/10 rounded-lg p-5 hover:border-[#ba1609]/30 transition-all group shadow-sm">
                  <span className="text-xs text-[#ba1609] uppercase tracking-wider">{t.type} • {t.duration}</span>
                  <h3 className="text-[#1a1012] font-medium mt-2 group-hover:text-[#ba1609] transition-colors">{t.name}</h3>
                  <p className="text-[#5b4d4f] text-sm mt-1 line-clamp-2">{t.description}</p>
                  <span className="text-[#ba1609] font-medium mt-3 inline-block text-sm">Contact for Quote</span>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}
    </SEOPageLayout>
  );
};

export default TourPage;
